// Administration → Accès Claude (MCP) (docs/wip/SPEC-MCP.md §3 and §6; only when the server
// reports features.mcp): the access keys of the office's MCP connector (GET /api/mcp/keys) — admin
// keys (Claude acts as the administrator who created it, every admin tool) and personal keys (made
// for one member: Claude acts as that member, with exactly his rights and his connector policy) —,
// « Créer une clé » (« Accès administrateur »: POST /api/mcp/keys {name}; « Accès personnel
// pour… »: {name, kind: 'personal', userId} → {key, url}: the connector address, the only time the
// secret leaves the server, shown once with the claude.ai steps and wiped when its window closes)
// and « Révoquer » (DELETE /api/mcp/keys/:id, confirmed). The list never holds a secret: only the
// name, the kind, who the key acts as, its creator, the dates and the 4-character hint.
import { h, button, input, field, badge, emptyState, icon, segmented, select } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { isConflict } from '../../api.js';
import { relativeTime, formatDateTime } from '../../format.js';
import { cleanLine, errorText, grantsSummary, policySummary, sortUsers } from './model.js';
import { sectionHead, loadingState, errorState, copyButton, formError, setBusy, focusKeeper, aid } from './common.js';
import './strings.js';

/** Longest key name the server accepts (server/mcp/keys.js MAX_KEY_NAME). */
export const KEY_NAME_MAX = 60;
/** Active admin keys allowed when the server does not say (server/mcp/keys.js MAX_ACTIVE_KEYS). */
export const DEFAULT_MAX_ACTIVE = 10;
/** Active personal keys of one member when the server does not say (MAX_PERSONAL_KEYS_PER_USER). */
export const DEFAULT_MAX_PERSONAL = 3;
/** Kinds of key (server/mcp/keys.js KEY_KINDS). */
export const KEY_KINDS = Object.freeze(['admin', 'personal']);
/** Why a key was revoked by the server (server/mcp/keys.js REVOKE_REASONS). */
export const REVOKE_REASONS = Object.freeze(['user_disabled', 'user_deleted', 'role_changed']);
/** Relative times ("il y a 5 min") are refreshed this often. */
const CLOCK_MS = 60_000;

const enc = encodeURIComponent;

/**
 * Active keys first, newest first (the server's order, applied again after a local change).
 * @param {object[]} keys
 * @returns {object[]}
 */
export function sortKeys(keys) {
  return [...(keys || [])].sort((a, b) => Number(!!a.revoked) - Number(!!b.revoked)
    || String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

/**
 * One key of the API in its plain form: `kind` 'admin' (also a key made before personal keys) or
 * 'personal'; `userId` / `userName` the person every call acts as (an admin key's creator when the
 * server does not say); `revoked` also from `revokedAt`; `userActive` false only when the server
 * says so (that person is gone, disabled or changed role: every call is refused); `userPending`
 * true while that person must still choose a password (refused until then).
 * @param {object} k
 * @returns {object}
 */
export function keyOf(k) {
  const kind = k.kind === 'personal' ? 'personal' : 'admin';
  const userId = typeof k.userId === 'string' && k.userId ? k.userId : kind === 'admin' ? k.createdBy ?? null : null;
  const userName = k.userName ?? (kind === 'admin' ? k.createdByName ?? null : null);
  return { ...k, kind, userId, userName, revoked: k.revoked === true || !!k.revokedAt };
}

/**
 * Normalises a GET /api/mcp/keys (or /api/me/mcp-keys) response (`{keys, maxActive,
 * maxPersonalPerUser}`; a bare array is accepted too).
 * @param {unknown} res
 * @returns {{keys: object[], maxActive: number, maxPersonalPerUser: number}}
 */
export function normalizeKeys(res) {
  const r = res && typeof res === 'object' ? res : {};
  const list = Array.isArray(res) ? res : Array.isArray(r.keys) ? r.keys : [];
  const keys = list
    .filter((k) => k && typeof k === 'object' && typeof k.id === 'string' && k.id)
    .map(keyOf);
  const maxActive = Number.isInteger(r.maxActive) && r.maxActive > 0 ? r.maxActive : DEFAULT_MAX_ACTIVE;
  const maxPersonalPerUser = Number.isInteger(r.maxPersonalPerUser) && r.maxPersonalPerUser > 0 ? r.maxPersonalPerUser : DEFAULT_MAX_PERSONAL;
  return { keys: sortKeys(keys), maxActive, maxPersonalPerUser };
}

/**
 * True when claude.ai cannot reach a connector address: not https, or a loopback host (the
 * server's publicUrl is still its default, e.g. OFFICE_PUBLIC_URL and the Railway domain unset).
 * @param {string} url
 * @returns {boolean}
 */
export function unreachableUrl(url) {
  let u;
  try { u = new URL(String(url ?? '')); } catch { return true; }
  const host = u.hostname.toLowerCase();
  return u.protocol !== 'https:' || host === 'localhost' || host.endsWith('.localhost') || /^127\./.test(host) || host === '[::1]' || host === '::1';
}

/** True when a key (normalised or not) is of `kind`. */
function isKind(k, kind) {
  return (k?.kind === 'personal' ? 'personal' : 'admin') === kind;
}

/**
 * Number of keys that still work: every one, or those of a kind, or the personal keys of one
 * member.
 * @param {object[]} keys
 * @param {{kind?: 'admin'|'personal', userId?: string}} [filter]
 */
export function activeKeyCount(keys, { kind, userId } = {}) {
  return (keys || []).filter((k) => k && !k.revoked && !k.revokedAt && (!kind || isKind(k, kind))
    && (userId === undefined || k.userId === userId)).length;
}

/**
 * Checks a key name like the server (required, 60 characters at most, unique case-insensitively
 * among the active keys of its kind: the admin keys together, a member's personal keys together —
 * Bob and Léa may each have a « claude.ai »).
 * @param {unknown} name
 * @param {object[]} [keys]
 * @param {{kind?: 'admin'|'personal', userId?: string|null}} [opts]
 * @returns {{ok: boolean, value: string, error: string|null}}
 */
export function validateKeyName(name, keys = [], { kind = 'admin', userId = null } = {}) {
  const value = cleanLine(name);
  let error = null;
  const same = (k) => k && !k.revoked && !k.revokedAt && isKind(k, kind) && (kind !== 'personal' || k.userId === userId);
  if (!value) error = t('adm.err.required');
  else if ([...value].length > KEY_NAME_MAX) error = t('adm.err.tooLong', { max: KEY_NAME_MAX });
  else if ((keys || []).some((k) => same(k) && String(k.name || '').toLocaleLowerCase('fr') === value.toLocaleLowerCase('fr'))) {
    error = t('adm.mcp.nameTaken');
  }
  return { ok: !error, value, error };
}

/**
 * The members a personal key can be made for, for the « Pour qui ? » list: every member (never an
 * administrator, who uses an admin key), sorted by name; a disabled account and one that already
 * holds `max` active personal keys are listed but not selectable.
 * @param {object[]} users GET /api/users
 * @param {object[]} keys the keys (normalised)
 * @param {number} [max] maxPersonalPerUser
 * @returns {Array<{id: string, name: string, user: object, active: number, disabled: boolean, full: boolean, label: string}>}
 */
export function keyOwners(users, keys, max = DEFAULT_MAX_PERSONAL) {
  return sortUsers((Array.isArray(users) ? users : []).filter((u) => u && typeof u.id === 'string' && u.role !== 'admin'))
    .map((u) => {
      const name = u.displayName || u.username || u.id;
      const active = activeKeyCount(keys, { kind: 'personal', userId: u.id });
      const disabled = u.disabled === true;
      const full = active >= max;
      const label = disabled ? t('adm.mcp.ownerDisabled', { name }) : full ? t('adm.mcp.ownerAtMax', { name, max }) : name;
      return { id: u.id, name, user: u, active, disabled, full, label };
    });
}

/**
 * The steps to add the connector on claude.ai (shared with « Mon accès Claude (MCP) »): the path,
 * the five steps (name « Maeck Office », the address, « Aucune connexion »), what to do when
 * claude.ai asks to sign in, and where the connector shows up next.
 * @param {{title?: string}} [opts]
 * @returns {HTMLElement}
 */
export function connectSteps({ title = t('adm.mcp.steps.title') } = {}) {
  return h('section', { class: 'adm-mcp-steps' },
    h('h4', { class: 'adm-subtitle mono-label' }, title),
    h('p', { class: 'adm-mcp-path' }, t('adm.mcp.steps.path')),
    h('ol', { class: 'adm-temp-list adm-mcp-steps-list' }, [1, 2, 3, 4, 5].map((i) => h('li', {}, t(`adm.mcp.steps.${i}`)))),
    h('p', { class: 'field-hint' }, t('adm.mcp.steps.trouble')),
    h('p', { class: 'field-hint' }, t('adm.mcp.steps.after')));
}

/**
 * Shows the connector address of a new key once: copy button, warning, the claude.ai steps. The
 * address only lives in this dialog: it is wiped from the input and the DOM when it closes. Not
 * dismissible by Esc, the backdrop or ×: a stray key would lose the address before it is copied;
 * only « J’ai copié l’adresse » closes it (and the sheet closing). For a personal key (`forName`),
 * the warning says to hand it over to that person, whose own claude.ai account takes it.
 * @param {object} ctx
 * @param {{name?: string}} key
 * @param {string} url
 * @param {{forName?: string|null}} [opts]
 * @returns {import('../overlay.js').Layer}
 */
export function showConnectorUrl(ctx, key, url, { forName = null } = {}) {
  const urlInput = h('input', {
    class: 'input adm-mcp-url', type: 'text', readOnly: true, value: url, autocomplete: 'off',
    spellcheck: false, 'aria-label': t('adm.mcp.secret.label'),
  });
  urlInput.addEventListener('focus', () => urlInput.select?.());
  const copy = copyButton(() => urlInput.value, { toast: ctx.toast, selectEl: urlInput, size: 'md', variant: 'primary' });
  const row = h('div', { class: 'adm-temp-row adm-mcp-url-row' }, urlInput, copy);
  let layer = null;
  const done = button(t('adm.mcp.secret.done'), { variant: 'secondary', onClick: () => layer?.close('confirm') });
  const content = h('div', { class: 'stack adm-mcp-secret' },
    h('p', { class: 'adm-temp-once' }, t('adm.mcp.secret.once')),
    row,
    unreachableUrl(url)
      ? h('p', { class: 'adm-alert is-danger', role: 'alert' }, icon('alert', { size: 16 }), h('span', {}, t('adm.mcp.secret.unreachable')))
      : null,
    h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('alert', { size: 16 }),
      h('span', {}, forName ? t('adm.mcp.secret.warnOther', { name: forName }) : t('adm.mcp.secret.warn'))),
    connectSteps(forName ? { title: t('adm.mcp.steps.titleFor', { name: forName }) } : {}));
  layer = ctx.overlay.openModal({
    title: t('adm.mcp.secret.title'),
    subtitle: forName ? t('adm.mcp.secret.subtitleFor', { name: key?.name || '', owner: forName }) : t('adm.mcp.secret.subtitle', { name: key?.name || '' }),
    size: 'md',
    content, footer: [done], initialFocus: copy, className: 'adm-temp-modal adm-mcp-modal', dismissible: false,
  });
  // The secret only lives in this dialog: empty the field and drop it from the DOM on close.
  layer.onClose?.(() => {
    urlInput.value = '';
    row.replaceChildren();
  });
  return layer;
}

/**
 * Creates the « Accès Claude (MCP) » tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, openCreate(opts?: {kind?: string, userId?: string}): object, destroy(): void}}
 */
export function createMcpView(ctx) {
  const me = () => ctx.store.get().me;
  let data = null;           // {keys, maxActive, maxPersonalPerUser}
  let error = null;
  let destroyed = false;
  let formLayer = null;      // the « Nouvelle clé » form, while open
  let secretLayer = null;    // the address shown once, while open
  let users = null;          // GET /api/users (« Pour qui ? »), loaded when a personal key is chosen
  let usersError = null;
  let usersPromise = null;
  const revoking = new Set();
  const keeper = focusKeeper();

  const newBtn = button(t('adm.mcp.new'), { variant: 'primary', icon: 'key', onClick: () => openCreate() });
  const head = sectionHead({ title: t('adm.mcp.title'), intro: t('adm.mcp.intro'), actions: [newBtn] });
  const notes = h('div', { class: 'adm-mcp-notes' },
    h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('shield', { size: 16 }), h('span', {}, t('adm.mcp.secretWarn'))),
    h('p', { class: 'adm-note' }, t('adm.mcp.agentsNote')));
  const fullNote = h('p', { class: 'adm-alert is-info', role: 'status', hidden: true });
  const body = h('div', { class: 'adm-body' }, loadingState());
  const el = h('section', { class: 'adm-view adm-mcp' }, head, notes, fullNote, body);

  async function load({ quiet = false } = {}) {
    try {
      const res = await ctx.api.get('/api/mcp/keys');
      if (destroyed) return;
      data = normalizeKeys(res);
      error = null;
    } catch (err) {
      if (destroyed) return;
      if (!quiet || !data) error = err;
    }
    render();
  }

  /** The accounts for « Pour qui ? » (once per tab; a failure is retried at the next form). */
  function loadUsers() {
    if (!usersPromise) {
      usersPromise = ctx.api.get('/api/users').then((list) => {
        users = Array.isArray(list) ? list : Array.isArray(list?.users) ? list.users : [];
        usersError = null;
      }, (err) => {
        usersError = err;
        usersPromise = null;
      });
    }
    return usersPromise;
  }

  function upsert(key) {
    if (!key?.id || !data) return;
    const k = keyOf(key);
    const keys = data.keys.some((x) => x.id === k.id) ? data.keys.map((x) => (x.id === k.id ? k : x)) : [...data.keys, k];
    data = { ...data, keys: sortKeys(keys) };
  }

  const adminFull = () => !!data && activeKeyCount(data.keys, { kind: 'admin' }) >= data.maxActive;

  // --- rendering --------------------------------------------------------------------------------
  function render() {
    const restore = keeper.begin();
    if (error && !data) {
      head.setCount('');
      newBtn.disabled = true;
      fullNote.hidden = true;
      body.replaceChildren(errorState(error, {
        onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.mcp.unavailable'),
      }));
      return;
    }
    if (!data) return;
    // The admin keys have their own cap; personal keys stay possible when it is reached.
    const full = adminFull();
    head.setCount(t('adm.mcp.count', { n: activeKeyCount(data.keys) }));
    newBtn.disabled = false;
    fullNote.hidden = !full;
    fullNote.textContent = full ? t('adm.mcp.full', { max: data.maxActive }) : '';
    if (!data.keys.length) {
      body.replaceChildren(emptyState({
        icon: 'key', title: t('adm.mcp.empty.title'), text: t('adm.mcp.empty.text'),
        action: button(t('adm.mcp.new'), { variant: 'primary', icon: 'key', onClick: () => openCreate() }),
      }));
      restore();
      return;
    }
    const now = Date.now();
    const th = (key) => h('th', { scope: 'col' }, t(`adm.mcp.col.${key}`));
    body.replaceChildren(h('div', { class: 'adm-table-wrap' },
      h('table', { class: 'table adm-table adm-mcp-table' },
        h('thead', {}, h('tr', {}, th('name'), th('kind'), th('owner'), th('createdAt'), th('lastUsed'), th('hint'),
          h('th', { scope: 'col' }, h('span', { class: 'sr-only' }, t('adm.actions'))))),
        h('tbody', {}, data.keys.map((k) => keyRow(k, now))))));
    restore();
  }

  function timeEl(v, now) {
    return v
      ? h('time', { datetime: String(v), title: formatDateTime(v) }, relativeTime(v, now))
      : h('span', { class: 'muted' }, t('adm.never'));
  }

  function revokedTitle(k) {
    if (!k.revokedAt) return undefined;
    const when = formatDateTime(k.revokedAt);
    return REVOKE_REASONS.includes(k.revokedReason)
      ? t('adm.mcp.revokedWhy', { when, reason: t(`adm.mcp.reason.${k.revokedReason}`) })
      : t('adm.mcp.revokedOn', { when });
  }

  function keyRow(k, now) {
    const self = !!me()?.id && k.userId === me().id;
    // The names are stored with the key (they stay readable once the account is gone).
    const owner = k.userName || (self ? me().displayName || me().username : k.userId) || '—';
    const creator = k.createdByName || k.createdBy || '';
    const inactive = !k.revoked && k.userActive === false;
    const pending = !k.revoked && !inactive && k.userPending === true;
    const busy = revoking.has(k.id);
    const action = k.revoked
      ? badge(t('adm.mcp.revokedBadge'), 'neutral', { title: revokedTitle(k) })
      : keeper.reg(`revoke:${k.id}`, button(t('adm.mcp.revoke'), {
        variant: 'ghost', size: 'sm', class: 'adm-del', ariaLabel: t('adm.mcp.revokeAria', { name: k.name }),
        disabled: busy, busy, onClick: () => revoke(k),
      }));
    return h('tr', { class: ['adm-mcp-row', { 'is-revoked': k.revoked, 'is-inactive': inactive }], dataset: { id: k.id, kind: k.kind } },
      h('td', { class: 'adm-cell-main', 'data-label': t('adm.mcp.col.name') },
        h('span', { class: 'adm-mcp-name' }, icon('key', { size: 14 }), h('span', {}, k.name || '—'))),
      h('td', { 'data-label': t('adm.mcp.col.kind') },
        badge(t(`adm.mcp.kind.${k.kind}`), k.kind === 'admin' ? 'dark' : 'info', { class: 'adm-mcp-kind', title: t(`adm.mcp.kindTitle.${k.kind}`) })),
      h('td', { class: 'adm-mcp-ownercell', 'data-label': t('adm.mcp.col.owner') },
        h('span', { class: 'adm-mcp-owner' },
          h('span', { class: 'adm-mcp-creator' }, owner, self ? h('span', { class: 'adm-you mono-label' }, t('adm.you')) : null),
          inactive ? badge(t('adm.mcp.ownerInactive'), 'amber', { class: 'adm-mcp-inactive', title: t('adm.mcp.ownerInactiveHint') }) : null,
          pending ? badge(t('adm.mcp.ownerPending'), 'amber', { class: 'adm-mcp-pending', title: t('adm.mcp.ownerPendingHint', { name: owner }) }) : null),
        // A personal key is made by an administrator for someone else: say who.
        creator && k.createdBy !== k.userId ? h('span', { class: 'adm-mcp-by muted' }, t('adm.mcp.createdByOther', { name: creator })) : null),
      h('td', { 'data-label': t('adm.mcp.col.createdAt') }, timeEl(k.createdAt, now)),
      h('td', { 'data-label': t('adm.mcp.col.lastUsed') }, timeEl(k.lastUsedAt, now)),
      h('td', { 'data-label': t('adm.mcp.col.hint') },
        h('code', { class: 'adm-code', title: t('adm.mcp.hintTitle') }, t('adm.mcp.hint', { hint: k.hint || '????' }))),
      h('td', { class: 'adm-cell-actions' }, action));
  }

  // --- revoke -----------------------------------------------------------------------------------
  async function revoke(k) {
    if (revoking.has(k.id) || k.revoked) return;
    const ok = await ctx.overlay.confirm({
      title: t('adm.mcp.revokeTitle', { name: k.name }), message: t('adm.mcp.revokeText'),
      confirmLabel: t('adm.mcp.revokeOk'), danger: true,
    });
    if (!ok || destroyed) return;
    revoking.add(k.id);
    render();
    try {
      const out = await ctx.api.del(`/api/mcp/keys/${enc(k.id)}`);
      if (destroyed) return;
      upsert(out?.key?.id ? out.key : { ...k, revoked: true, revokedAt: new Date().toISOString() });
      ctx.toast.success(t('adm.mcp.revoked', { name: k.name }));
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      revoking.delete(k.id);
      if (!destroyed) {
        render();
        newBtn.focus?.();
        load({ quiet: true });
      }
    }
  }

  // --- create -----------------------------------------------------------------------------------
  /**
   * The « Nouvelle clé d’accès » form: a name, then « Accès administrateur » (the default; Claude
   * acts as me) or « Accès personnel pour… » a member (Claude acts as him, with exactly his rights).
   * @param {{kind?: 'admin'|'personal', userId?: string}} [opts] preselect a personal key for a member
   */
  function openCreate({ kind: wanted = 'admin', userId: wantedUser = null } = {}) {
    if (formLayer?.isOpen?.()) return formLayer;
    const noAdmin = adminFull();
    let kind = wanted === 'personal' || noAdmin ? 'personal' : 'admin';
    const nameInput = input({ name: 'name', maxLength: KEY_NAME_MAX, placeholder: t('adm.mcp.namePlaceholder'), autocomplete: 'off', autofocus: true });
    const fName = field(t('adm.mcp.name'), nameInput, { hint: t('adm.mcp.nameHint') });
    const kindLabelId = aid('kind');
    const kindHint = h('p', { class: 'field-hint adm-mcp-kindhint', 'aria-live': 'polite' });
    const kindSeg = segmented(KEY_KINDS.map((v) => ({ value: v, label: t(`adm.mcp.kindOpt.${v}`), icon: v === 'admin' ? 'shield' : 'user' })), {
      value: kind, label: t('adm.mcp.kind'), onChange: (v) => { kind = v; sync(); },
    });
    kindSeg.setAttribute('aria-labelledby', kindLabelId);
    kindSeg.classList.add('adm-mcp-kinds');
    // The admin keys are at their cap: only a personal key can be made.
    for (const b of kindSeg.childNodes) if (b.dataset?.value === 'admin') b.disabled = noAdmin;
    const kindField = h('div', { class: 'field adm-mcp-kindfield' },
      h('span', { class: 'field-label', id: kindLabelId }, t('adm.mcp.kind')), kindSeg, kindHint,
      noAdmin ? h('p', { class: 'adm-note is-amber' }, t('adm.mcp.adminFull', { max: data?.maxActive ?? DEFAULT_MAX_ACTIVE })) : null);
    const ownerSel = select([{ value: '', label: t('adm.mcp.ownerLoading') }], { name: 'owner', class: 'adm-mcp-owner-select', disabled: true });
    const fOwner = field(t('adm.mcp.owner'), ownerSel, { hint: t('adm.mcp.ownerHint', { max: data?.maxPersonalPerUser ?? DEFAULT_MAX_PERSONAL }) });
    const ownerNote = h('p', { class: 'field-hint adm-mcp-ownernote', hidden: true });
    const rights = h('p', { class: 'adm-note adm-mcp-rights', role: 'status', hidden: true });
    const ownerBox = h('div', { class: 'stack adm-mcp-ownerbox' }, fOwner, ownerNote, rights);
    const warnText = h('span', {});
    const warn = h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('shield', { size: 16 }), warnText);
    const err = formError();
    const formId = aid('form');
    let layer = null;
    const submitBtn = button(t('adm.mcp.create'), { variant: 'primary', type: 'submit' });
    submitBtn.setAttribute('form', formId);
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });

    const chosen = () => (kind === 'personal' && ownerSel.value ? (users || []).find((u) => u.id === ownerSel.value) || null : null);
    let listNote = ''; // why no member can be chosen (loading failed, none), shown under the list

    function fillOwners() {
      if (!users) {
        ownerSel.replaceChildren(h('option', { value: '' }, usersError ? t('common.error') : t('adm.mcp.ownerLoading')));
        ownerSel.disabled = true;
        listNote = usersError ? t('adm.mcp.ownerUnavailable', { error: errorText(usersError) }) : '';
        return;
      }
      const owners = keyOwners(users, data?.keys || [], data?.maxPersonalPerUser ?? DEFAULT_MAX_PERSONAL);
      const previous = ownerSel.value || wantedUser || '';
      const ok = (id) => owners.some((o) => o.id === id && !o.disabled && !o.full);
      ownerSel.replaceChildren(h('option', { value: '' }, t('adm.mcp.ownerPick')),
        ...owners.map((o) => h('option', { value: o.id, disabled: o.disabled || o.full }, o.label)));
      ownerSel.value = ok(previous) ? previous : '';
      ownerSel.disabled = !owners.length;
      listNote = owners.length ? '' : t('adm.mcp.ownerNone');
    }

    /** The note under the list (why none, or the chosen member's temporary password) and his rights. */
    function syncOwner() {
      const user = chosen();
      const name = user ? user.displayName || user.username : '';
      const note = listNote || (user?.mustChangePassword ? t('adm.mcp.ownerTempPw', { name }) : '');
      ownerNote.textContent = note;
      ownerNote.hidden = !note;
      if (!user) {
        rights.hidden = true;
        return;
      }
      const state = ctx.store.get();
      rights.hidden = false;
      rights.textContent = t('adm.mcp.ownerRights', {
        grants: grantsSummary(user, state.office, { max: 3 }), conns: policySummary(user.connectorPolicy, state.connectors || []),
      });
    }

    function sync() {
      const personal = kind === 'personal';
      kindHint.textContent = t(`adm.mcp.kindHint.${kind}`);
      ownerBox.hidden = !personal;
      warnText.textContent = personal ? t('adm.mcp.personalWarn') : t('adm.mcp.secretWarn');
      fOwner.setError(null);
      if (personal) {
        fillOwners();
        syncOwner();
        if (!users) {
          loadUsers().then(() => {
            if (layer?.closed || destroyed) return;
            fillOwners();
            syncOwner();
          });
        }
      }
    }

    ownerSel.addEventListener('change', () => { fOwner.setError(null); syncOwner(); });

    async function submit(e) {
      e?.preventDefault?.();
      if (submitBtn.disabled) return;
      const personal = kind === 'personal';
      const owner = chosen();
      const res = validateKeyName(nameInput.value, data?.keys || [], { kind, userId: owner?.id ?? null });
      fName.setError(res.error);
      fOwner.setError(personal && !owner ? t('adm.mcp.ownerRequired') : null);
      if (!res.ok) { nameInput.focus?.(); return; }
      if (personal && !owner) { ownerSel.focus?.(); return; }
      err.set(null);
      setBusy(submitBtn, true);
      const forName = personal ? owner.displayName || owner.username : null;
      let out = null;
      try {
        out = await ctx.api.post('/api/mcp/keys', personal ? { name: res.value, kind: 'personal', userId: owner.id } : { name: res.value });
      } catch (ex) {
        if (layer?.closed) return;
        setBusy(submitBtn, false);
        const msg = ex.message || '';
        if (personal && (ex.status === 404 || (ex.status === 400 && /compte|membre|personne|administrateur|userId/i.test(msg)))) {
          fOwner.setError(errorText(ex));
          ownerSel.focus?.();
        } else if (ex.status === 400 || (isConflict(ex) && /appelle|nom|déjà une clé/i.test(msg))) {
          fName.setError(errorText(ex));
          nameInput.focus?.();
        } else err.set(errorText(ex));
        return;
      }
      const key = out?.key?.id ? out.key : null;
      const url = typeof out?.url === 'string' ? out.url : '';
      out = null; // the response carried the secret: keep no reference to it
      layer?.close('api');
      if (!destroyed) {
        if (key) upsert(key);
        render();
      }
      ctx.toast.success(personal ? t('adm.mcp.createdFor', { name: key?.name || res.value, owner: forName }) : t('adm.mcp.created', { name: key?.name || res.value }));
      // Shown even when the sheet closed meanwhile (the overlay is global): the address exists
      // nowhere else, and a key whose address nobody saw would only hold one of the active slots.
      if (url) {
        const shown = showConnectorUrl(ctx, key || { name: res.value }, url, { forName });
        if (!destroyed) {
          secretLayer = shown;
          shown.onClose?.(() => { if (secretLayer === shown) secretLayer = null; });
        }
      }
      if (!destroyed) load({ quiet: true });
    }

    const form = h('form', { id: formId, class: 'stack adm-form', novalidate: true, onSubmit: submit },
      fName, kindField, ownerBox, warn, err);
    sync();
    layer = ctx.overlay.openModal({
      title: t('adm.mcp.createTitle'), subtitle: t('adm.mcp.createSubtitle'), size: 'sm', content: form,
      footer: [cancel, submitBtn], className: 'adm-modal',
    });
    formLayer = layer;
    layer.onClose?.(() => { if (formLayer === layer) formLayer = null; });
    return layer;
  }

  // Relative times drift: refresh the <time> texts every minute (focus and scroll stay put).
  const clock = setInterval(() => {
    if (destroyed || !data) return;
    const now = Date.now();
    for (const tm of body.querySelectorAll('time')) tm.textContent = relativeTime(tm.getAttribute('datetime'), now);
  }, CLOCK_MS);
  load();
  return {
    el,
    reload: () => load({ quiet: true }),
    openCreate,
    destroy() {
      destroyed = true;
      clearInterval(clock);
      // Closing the sheet closes the address window too (and wipes the secret).
      secretLayer?.close?.('route');
      formLayer?.close?.('route');
      secretLayer = null;
      formLayer = null;
    },
  };
}
